import React from 'react';
import { Link } from 'react-router-dom';
import { useLatestMovie } from '../hooks/useLatestMovie';

/**
 * Enhanced Latest Movie Display Component
 * Shows how to use dynamic poster URLs by ID
 */
const LatestMovieDisplay = () => {
  const { latestMovie, loading, error, getPosterUrlBySize } = useLatestMovie();

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading latest movie...</span>
        </div>
        <p className="mt-3 text-white">Loading latest movie...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning">
        <h5>Unable to load latest movie</h5>
        <p>{error}</p>
      </div>
    );
  }

  if (!latestMovie) {
    return (
      <div className="alert alert-info">
        <h5>No latest movie available</h5>
        <p>Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="latest-movie-display">
      {/* Main Movie Poster */}
      <div className="movie-poster-container mb-4">
        <Link to={`/movie-details/${latestMovie.id}`}>
          <img 
            src={latestMovie.poster_url} // Dynamic poster URL by ID
            className="img-fluid" 
            alt={latestMovie.title}
            style={{maxHeight: '400px', objectFit: 'contain'}}
            onError={(e) => {
              // Fallback to smaller size if main image fails
              e.target.src = latestMovie.poster_url_medium || latestMovie.poster_url_small;
            }}
          />
        </Link>
      </div>

      {/* Movie Details */}
      <div className="movie-details">
        <h3 className="text-white mb-2">{latestMovie.title}</h3>
        <p className="text-muted mb-3">{latestMovie.overview}</p>
        
        {/* Movie Info */}
        <div className="movie-info d-flex flex-wrap gap-3 mb-3">
          <span className="badge badge-secondary">
            <i className="fa fa-calendar mr-1"></i>
            {latestMovie.year}
          </span>
          <span className="badge badge-secondary">
            <i className="fa fa-clock-o mr-1"></i>
            {latestMovie.duration}
          </span>
          <span className="badge badge-secondary">
            <i className="fa fa-star mr-1"></i>
            {latestMovie.rating}/10
          </span>
          <span className="badge badge-secondary">
            {latestMovie.ageRating}
          </span>
        </div>

        {/* Rating Stars */}
        <div className="movie-rating mb-3">
          <div className="d-flex align-items-center">
            <span className="text-white mr-2">Rating:</span>
            <div className="stars">
              {[...Array(5)].map((_, i) => (
                <i 
                  key={i}
                  className={`fa ${
                    i < Math.floor(latestMovie.vote_average / 2) 
                      ? 'fa-star text-warning' 
                      : i < latestMovie.vote_average / 2 
                        ? 'fa-star-half-o text-warning' 
                        : 'fa-star-o text-muted'
                  }`}
                ></i>
              ))}
              <span className="text-white ml-2">
                ({latestMovie.vote_average}/10)
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="movie-actions">
          <Link 
            to={`/movie-details/${latestMovie.id}`}
            className="btn btn-primary me-2"
          >
            <i className="fa fa-play mr-1"></i>
            Watch Now
          </Link>
          <Link 
            to={`/movie-details/${latestMovie.id}`}
            className="btn btn-outline-light"
          >
            <i className="fa fa-info-circle mr-1"></i>
            More Info
          </Link>
        </div>
      </div>

      {/* Responsive Images Demo */}
      <div className="responsive-images-demo mt-4">
        <h5 className="text-white mb-3">Responsive Poster Sizes:</h5>
        <div className="row">
          <div className="col-md-2">
            <div className="text-center">
              <img 
                src={getPosterUrlBySize('thumbnail')} 
                className="img-fluid mb-2" 
                alt="Thumbnail"
                style={{maxHeight: '100px'}}
              />
              <small className="text-muted">Thumbnail (92px)</small>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center">
              <img 
                src={getPosterUrlBySize('small')} 
                className="img-fluid mb-2" 
                alt="Small"
                style={{maxHeight: '100px'}}
              />
              <small className="text-muted">Small (154px)</small>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center">
              <img 
                src={getPosterUrlBySize('medium')} 
                className="img-fluid mb-2" 
                alt="Medium"
                style={{maxHeight: '100px'}}
              />
              <small className="text-muted">Medium (342px)</small>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center">
              <img 
                src={getPosterUrlBySize('large')} 
                className="img-fluid mb-2" 
                alt="Large"
                style={{maxHeight: '100px'}}
              />
              <small className="text-muted">Large (500px)</small>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center">
              <img 
                src={getPosterUrlBySize('original')} 
                className="img-fluid mb-2" 
                alt="Original"
                style={{maxHeight: '100px'}}
              />
              <small className="text-muted">Original</small>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Info */}
      <div className="debug-info mt-4 p-3 bg-dark rounded">
        <h6 className="text-info">Debug Info:</h6>
        <p className="text-muted small">
          <strong>Movie ID:</strong> {latestMovie.id}<br/>
          <strong>Poster Path:</strong> {latestMovie.poster_path}<br/>
          <strong>Main Poster URL:</strong> {latestMovie.poster_url}<br/>
          <strong>Backdrop URL:</strong> {latestMovie.backdrop_url}
        </p>
      </div>
    </div>
  );
};

export default LatestMovieDisplay;
